import React from 'react';
import styled from 'styled-components';

interface Props {
  children: React.ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  isCompatibilityError?: boolean;
}

const ErrorContainer = styled.div`
  padding: 2rem;
  max-width: 600px;
  margin: 2rem auto;
  text-align: center;
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;

const ErrorTitle = styled.h1`
  color: #e74c3c;
  margin-bottom: 1rem;
`;

const ErrorMessage = styled.p`
  color: #666;
  margin-bottom: 1.5rem;
`;

const ErrorDetails = styled.details`
  text-align: left;
  margin-bottom: 1.5rem;
`;

const ErrorSummary = styled.summary`
  cursor: pointer;
  color: #3498db;
`;

const ErrorPre = styled.pre`
  background-color: #f8f9fa;
  padding: 1rem;
  border-radius: 4px;
  overflow: auto;
  margin-top: 0.5rem;
`;

const ReloadButton = styled.button`
  background-color: #3498db;
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 4px;
  cursor: pointer;
  font-size: 1rem;

  &:hover {
    background-color: #2980b9;
  }
`;

const CompatibilityMessage = styled.div`
  background-color: #fff3cd;
  border: 1px solid #ffeeba;
  color: #856404;
  padding: 1rem;
  border-radius: 4px;
  margin-bottom: 1.5rem;
  text-align: left;
`;

export class ErrorBoundary extends React.Component<Props, State> {
  public constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  public static getDerivedStateFromError(error: Error): State {
    const isCompatibilityError = error.message.includes('Unknown name provided');
    return {
      hasError: true,
      error,
      isCompatibilityError
    };
  }

  public componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    console.error('Error caught by boundary:', error);
    console.error('Error info:', errorInfo);
  }

  public render(): React.ReactNode {
    if (this.state.hasError) {
      return (
        <ErrorContainer>
          <ErrorTitle>Something went wrong</ErrorTitle>
          {this.state.isCompatibilityError ? (
            <>
              <CompatibilityMessage>
                <strong>Case File Compatibility Issue</strong>
                <p>
                  This case was created for a different version of the application and cannot be opened in the current
                  version.
                </p>
              </CompatibilityMessage>
              <ErrorMessage>If you believe this is an error, please contact technical support.</ErrorMessage>
            </>
          ) : (
            <ErrorMessage>We apologize for the inconvenience. The application has encountered an error.</ErrorMessage>
          )}
          <ErrorDetails>
            <ErrorSummary>Error Details</ErrorSummary>
            <ErrorPre>{this.state.error?.toString()}</ErrorPre>
          </ErrorDetails>
          <ReloadButton onClick={() => window.location.reload()}>Reload Page</ReloadButton>
        </ErrorContainer>
      );
    }

    return this.props.children;
  }
}
